import {Button, FlagSizeType, openDialog, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';

import {head} from 'ramda';

import {useGetTagsForEntityQuery, useGetTagsQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {SkeletonLoadingBox} from '../SkeletonLoadingBox';
import {CreateOrEditTagForm} from './CreateOrEditTagForm';
import {EditableTag} from './EditableTag';

interface ListEditableTagsProps extends TestIdProps {
  resourceId: EntityResourceIds;
  recordId: string;
  size?: FlagSizeType;
  isLoading?: boolean;
  buttonVariant: 'secondary' | 'ghostLink';
  isTagDeletable?: boolean;
  isTagUpdatable?: boolean;
  isTagCreatable?: boolean;
  isTagReadable?: boolean;
  isTagAssignable?: boolean;
  isTagUnassignable?: boolean;
}

const CONTENTS_STYLES = {display: 'contents'} as const;

export function ListEditableTags(props: ListEditableTagsProps) {
  const {tagId, isLoading: isTagsLoading} = useGetTagsQuery(
    {resourceId: props.resourceId},
    {selectFromResult: ({data, isLoading}) => ({tagId: head(data ?? [])?.id, isLoading})}
  );
  const {data: entityTags, isLoading: isEntityTagsLoading} = useGetTagsForEntityQuery(props);

  const createTag = () => {
    if (!tagId) {
      showNotification.error();
      return;
    }

    openDialog(
      <CreateOrEditTagForm
        recordId={props.recordId}
        resourceId={props.resourceId}
        isCreatable={props.isTagCreatable}
        isDeletable={props.isTagDeletable}
      />,
      {
        title: i18n.t('general.labels.assignTag'),
        scrollBehavior: 'outside',
        size: 'small',
      }
    );
  };

  return (
    <Show when={props.isTagReadable}>
      <SkeletonLoadingBox
        isLoading={isTagsLoading || isEntityTagsLoading || props.isLoading}
        width={22}
        height={6}
        contentStyles={CONTENTS_STYLES}
      >
        <Show when={tagId}>
          {(tagId) => (
            <>
              {entityTags?.map((tag, index) => (
                <EditableTag
                  {...props}
                  tagId={tagId}
                  key={`tag-[${tag.tagId}]-${tag.valueName}`}
                  color={tag.valueColor}
                  label={tag.valueName}
                  tagValueId={tag.valueId}
                  size={props.size}
                  data-testid={suffixTestId(`tag-[${index}]-[${tag.tagId}]`, props)}
                  isTagDeletable={props.isTagDeletable}
                  isTagUpdatable={
                    props.isTagUpdatable && props.isTagAssignable && props.isTagUnassignable
                  }
                  isTagCreatable={props.isTagCreatable}
                  isTagUnassignable={props.isTagUnassignable}
                />
              ))}
              <Show when={props.isTagAssignable}>
                <Button
                  title={i18n.t('general.actions.createNewTag')}
                  onClick={createTag}
                  variant={props.buttonVariant}
                  leftIcon={props.buttonVariant === 'ghostLink' ? 'content/add_circle' : undefined}
                  size="small"
                  data-testid={suffixTestId('add-tag', props)}
                />
              </Show>
            </>
          )}
        </Show>
      </SkeletonLoadingBox>
    </Show>
  );
}
